/**
 * App.tsx
 *
 * Renders the application within providers, handles auth redirect completion,
 * shows a development-only theme toggle, and conditionally renders either the
 * dashboard (authenticated) or login (unauthenticated) views.
 */

import { useState, useEffect, Suspense, lazy } from 'react';
import { AppProviders, useApp } from './providers';
import { useLanguage } from './providers/LanguageProvider';
import { useNavigationBarTheming } from './providers/useNavigationBarTheming';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { completeRedirectSignIn } from '../lib/firebase';
import { Capacitor } from '@capacitor/core';
import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import '../utils/adminUtils'; // Load admin utilities for development

// Lazy load page components for code splitting
const Login = lazy(() => import('../pages/login'));
const Dashboard = lazy(() => import('../pages/dashboard'));
const ControlCenter = lazy(() => import('../pages/controlCenter'));
const PrivacyPolicyPage = lazy(() => import('../pages/legal/privacy'));
const TermsOfServicePage = lazy(() => import('../pages/legal/terms'));
const SupportPage = lazy(() => import('../pages/support'));
import '../utils/adminUtils'; // Load admin utilities for development

function AppContent() {
  const { user, userRole, loading } = useApp();
  const { t } = useLanguage();
  const [serverError, setServerError] = useState<string | null>(null);
  
  // Add basic startup logging
  console.log('[AstroShifts] App starting up, user:', !!user, 'userRole:', userRole, 'loading:', loading);
  
  // Use the navigation bar theming hook
  useNavigationBarTheming();

  useEffect(() => {
    // Only attempt to complete redirect flow on web; native uses plugin-based sign-in
    if (Capacitor.getPlatform() !== 'web') return;
    (async () => {
      try {
        await completeRedirectSignIn();
        // onAuthChange in AuthProvider will handle the user state update
      } catch (e: unknown) {
        const error = e as Error;
        setServerError(error?.message || t('login', 'signInError'));
      }
    })();
  }, []);

  if (loading) {
    return <LoadingSpinner message={`${t('globals', 'loading')} ${t('globals', 'appName')}...`} />;
  }

  const handleLogout = async () => {
    try {
      await signOut(auth);
      // The auth state will be updated automatically by onAuthStateChanged
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Helper function to get the default page based on user role
  const getDefaultPage = () => {
    // Show loading while auth is initializing
    if (loading) {
      return <LoadingSpinner message="Initializing AstroShifts..." />;
    }
    
    // No user = show login
    if (!user) {
      return <Login serverError={serverError || undefined} />;
    }
    
    // User exists but role not loaded yet = show loading
    if (userRole === null) {
      return <LoadingSpinner message="Loading your workspace..." />;
    }
    
    // Route based on role
    if (userRole === 'admin') return <ControlCenter onLogout={handleLogout} />;
    return <Dashboard onLogout={handleLogout} />;
  };

  // Helper function to check if user can access control center
  const canAccessControlCenter = () => {
    return user && userRole === 'admin';
  };

  return (
    <div className="relative">
      <BrowserRouter>
        <Suspense fallback={<LoadingSpinner message="Loading page..." />}>
          <Routes>
            <Route
              path="/"
              element={getDefaultPage()}
            />
            {/* Public informational routes */}
            <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/legal/terms" element={<TermsOfServicePage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route
              path="/dashboard"
              element={
                user ? (
                  loading || userRole === null ? (
                    <LoadingSpinner message="Loading dashboard..." />
                  ) : (
                    <Dashboard onLogout={handleLogout} />
                  )
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/controlCenter"
              element={
                canAccessControlCenter() ? (
                  <ControlCenter onLogout={handleLogout} />
                ) : user && (loading || userRole === null) ? (
                  <LoadingSpinner message="Checking permissions..." />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default function App() {
  return (
    <AppProviders>
      <AppContent />
    </AppProviders>
  );
}
